import {
  CheckIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  DocumentTextIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";

import KnowledgeBaseCallout from "components/KnowledgeBaseCallout";
import PageHeader from "components/PageHeader";

import type { StartHubTaskStatus } from "./startHubStatus";

type StartHubProps = {
  caseId: string;
  vitalsStatus: StartHubTaskStatus;
  arrangementStatus: StartHubTaskStatus;
  consultationStatus: StartHubTaskStatus;
  showTimelineLink?: boolean;
  timelineHref: string;
  timelineState?: Record<string, unknown>;
};

type HubIcon = typeof DocumentTextIcon;

type StartHubTask = {
  id: "vitals" | "arrangements" | "consultation";
  title: string;
  description: string;
  readinessDescription?: string;
  duration?: string;
  icon: HubIcon;
  href: string;
  status: StartHubTaskStatus;
};

type Action =
  | { type: "link"; label: "Start" | "Resume"; href: string }
  | { type: "done" }
  | { type: "disabled"; label: "Start" };

function buildTasks({
  arrangementStatus,
  caseId,
  consultationStatus,
  vitalsStatus,
}: Pick<
  StartHubProps,
  "arrangementStatus" | "caseId" | "consultationStatus" | "vitalsStatus"
>): StartHubTask[] {
  return [
    {
      id: "vitals",
      title: "Vitals",
      duration: "10 min",
      description: "Provide the details we need for the official record.",
      icon: DocumentTextIcon,
      href: `/forms/vitals/${caseId}`,
      status: vitalsStatus,
    },
    {
      id: "arrangements",
      title: "Arrangements",
      duration: "15 min",
      description: "Choose your package, services, and selections.",
      icon: ClipboardDocumentListIcon,
      href: `/cases/${caseId}/arrange`,
      status: arrangementStatus,
    },
    {
      id: "consultation",
      title: "Consultation",
      description: "Schedule a call with your dedicated Care Specialist.",
      readinessDescription:
        "Available once Arrangements and Vitals are complete.",
      icon: PhoneIcon,
      href: `/cases/${caseId}/step/schedule-consultation`,
      status: consultationStatus,
    },
  ];
}

function taskAction(task: StartHubTask): Action {
  if (task.status === "done") return { type: "done" };
  if (task.status === "disabled") return { type: "disabled", label: "Start" };

  return {
    type: "link",
    label: task.status === "resume" ? "Resume" : "Start",
    href: task.href,
  };
}

function RowAction({ action, rowTitle }: { action: Action; rowTitle: string }) {
  const buttonClass =
    "inline-flex h-9 min-w-24 items-center justify-center rounded-lg px-4 text-center text-sm font-bold transition-colors";

  if (action.type === "done") {
    return (
      <div className="inline-flex min-w-24 items-center justify-center gap-2 text-center text-sm font-bold text-evergreen">
        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-evergreen text-white">
          <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
        Complete
      </div>
    );
  }

  const ariaLabel = `${action.label} ${rowTitle}`;

  if (action.type === "disabled") {
    return (
      <button
        type="button"
        disabled
        aria-label={ariaLabel}
        className={`${buttonClass} cursor-not-allowed bg-cantelope/45 text-soil/70`}
      >
        {action.label}
      </button>
    );
  }

  return (
    <Link
      to={action.href}
      aria-label={ariaLabel}
      className={`${buttonClass} bg-cantelope text-soil hover:bg-cantelope/80`}
    >
      {action.label}
    </Link>
  );
}

function TaskItem({ task }: { task: StartHubTask }) {
  const action = taskAction(task);
  const disabled = task.status === "disabled";
  const Icon = task.icon;

  return (
    <div className="grid gap-4 px-4 py-4 text-soil sm:grid-cols-[3.75rem_1fr_auto] sm:items-center sm:px-5">
      <div className="flex justify-center sm:justify-start">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-oat/35">
          <Icon className="h-5 w-5 text-evergreen" />
        </div>
      </div>

      <div className="min-w-0 text-center sm:text-left">
        <div className="flex flex-col items-center gap-2 sm:flex-row sm:flex-wrap">
          <h2 className="text-lg font-bold leading-tight text-evergreen sm:text-xl">
            {task.title}
          </h2>
          {task.duration && (
            <div className="flex items-center gap-1.5 text-base text-oat-700">
              <ClockIcon className="h-4 w-4" aria-hidden="true" />
              <span>{task.duration}</span>
            </div>
          )}
        </div>
        <p className="mt-1.5 text-base leading-snug">{task.description}</p>
        {disabled && (
          <p className="mt-2 inline-flex items-center gap-2 rounded-full bg-cantelope/15 px-3 py-1 text-sm font-semibold text-soil/75">
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-cantelope/70 text-soil">
              <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
            {task.readinessDescription ??
              "This isn't ready to be completed yet."}
          </p>
        )}
      </div>

      <div className="flex justify-center sm:justify-end">
        <RowAction action={action} rowTitle={task.title} />
      </div>
    </div>
  );
}

export default function StartHub({
  caseId,
  vitalsStatus,
  arrangementStatus,
  consultationStatus,
  showTimelineLink = false,
  timelineHref,
  timelineState,
}: StartHubProps) {
  const tasks = buildTasks({
    arrangementStatus,
    caseId,
    consultationStatus,
    vitalsStatus,
  });

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col">
      <PageHeader
        title="Let's get started"
        description="Here's what needs your attention right now. We'll guide you through each step, one at a time."
      />

      <section
        aria-label="Getting started steps"
        className="mt-6 overflow-hidden rounded-xl border border-oat-200 bg-white shadow-sm"
      >
        {tasks.map((task, index) => (
          <div key={task.id}>
            {index > 0 && <div className="border-t border-oat-100" />}
            <TaskItem task={task} />
          </div>
        ))}
      </section>

      <KnowledgeBaseCallout
        subject={{ kind: "case", id: caseId }}
        className="mt-6"
      />

      {showTimelineLink && (
        <div className="mt-6 flex justify-center">
          <Link
            to={timelineHref}
            state={timelineState}
            aria-label="Go to Timeline"
            className="inline-flex h-9 items-center justify-center rounded-lg bg-cantelope px-5 text-center text-sm font-bold text-soil transition-colors hover:bg-cantelope/80"
          >
            Go to Timeline
          </Link>
        </div>
      )}
    </div>
  );
}
